/**
 * init-animations.js  (hybrid: class hooks + data-* params)
 * ------------------------------------------------------------------
 * A small, attribute-driven GSAP animation library.
 *
 * HOOK CONVENTION
 *   - A `js-` prefixed CLASS turns a behavior on. It is a JS hook only —
 *     never style it in your SCSS. That keeps your visual classes and
 *     your behavior hooks orthogonal: rename a styling class freely and
 *     nothing here breaks.
 *   - `data-*` ATTRIBUTES pass per-element parameters. Only behaviors
 *     that actually take a value use them.
 *
 *   <h2 class="h2 js-scrub-each-word">A New Perspective on Taste</h2>
 *   <div class="card js-fade-up" data-fade-y="40" data-fade-delay="0.1">…</div>
 *   <h1 class="hero__title js-reveal-lines">Big masked headline</h1>
 *   <img class="hero__img js-parallax" data-parallax-speed="0.2" src="…">
 *
 * Requires GSAP 3.13+ (SplitText is free as of 3.13).
 *
 * BUILD (esbuild / your per-project toolchain):
 *   import "./init-animations.js";   // side-effect import; auto-runs on DOM ready
 *
 * NO-BUILD (CDN globals) alternative — delete the three import lines and
 * the registerPlugin() call, then load gsap.min.js, ScrollTrigger.min.js
 * and SplitText.min.js before this file. Everything else is unchanged.
 * ------------------------------------------------------------------
 */

// import gsap from "gsap";
// import { ScrollTrigger } from "gsap/ScrollTrigger";
// import { SplitText } from "gsap/SplitText";

// gsap.registerPlugin(ScrollTrigger, SplitText);

/* ------------------------------------------------------------------ *
 * Core infrastructure
 * ------------------------------------------------------------------ */

const REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Tracks elements we've already wired up so a re-run never double-inits.
const seen = new WeakSet();

// class name -> init function(element)
const registry = new Map();

/**
 * Register a behavior keyed to a `js-` prefixed class.
 * @param {string} className  Bare class name, e.g. "js-fade-up".
 * @param {(el: Element) => void} init  Runs once per matching element.
 */
function register(className, init) {
  registry.set(className, init);
}

/**
 * Scan a root node (default: document) and run every registered
 * behavior against elements that opt in and haven't been handled yet.
 * @param {ParentNode} [root=document]
 */
function initAnimations(root = document) {
  registry.forEach((init, className) => {
    root.querySelectorAll(`.${className}`).forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      init(el);
    });
  });
}

/**
 * Recalculate all ScrollTrigger positions. Call after you inject
 * content, swap Swiper slides, load images, toggle a tab, etc.
 * Also re-scans for brand-new animated elements.
 * @param {ParentNode} [root=document]
 */
function refresh(root = document) {
  initAnimations(root);
  ScrollTrigger.refresh();
}

/* ------------------------------------------------------------------ *
 * Behaviors
 *
 * Add your own with register("js-my-thing", (el) => { ... }). Read any
 * parameters off el.dataset — remember data-parallax-speed becomes
 * el.dataset.parallaxSpeed (data- dropped, hyphens -> camelCase).
 * Keep the reduced-motion branch so content is always readable.
 * ------------------------------------------------------------------ */

/**
 * .js-scrub-each-word
 * Words fade from dim -> full as the element scrolls through the
 * viewport, tied to the scrollbar (scrub) and staggered word by word.
 */
register("js-scrub-each-word", (el) => {
  if (REDUCED_MOTION) return; // native text already at full opacity

  SplitText.create(el, {
    type: "words",
    wordsClass: "word",
    autoSplit: true, // re-split cleanly on resize / font load
    onSplit(self) {
      // Returning the tween lets GSAP revert + rebuild it on each re-split.
      return gsap.fromTo(
        self.words,
        { opacity: 0.15 },
        {
          opacity: 1,
          ease: "none",
          stagger: 0.4,
          scrollTrigger: {
            trigger: el,
            start: "top 80%",
            end: "bottom 55%",
            scrub: true,
          },
        }
      );
    },
  });
});

/**
 * .js-reveal-lines
 * Masked line-by-line reveal: each line slides up out of an
 * overflow-hidden wrapper. Plays once on enter.
 */
register("js-reveal-lines", (el) => {
  if (REDUCED_MOTION) return;

  SplitText.create(el, {
    type: "lines",
    linesClass: "line",
    mask: "lines", // GSAP 3.13+ wraps each line in an overflow:clip container
    autoSplit: true,
    onSplit(self) {
      return gsap.from(self.lines, {
        yPercent: 110,
        duration: 0.8,
        ease: "power3.out",
        stagger: 0.12,
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true,
        },
      });
    },
  });
});

/**
 * .js-fade-up
 * Fades in and rises when it enters the viewport. Plays once.
 * Optional params:
 *   data-fade-y="40"        distance in px (default 24)
 *   data-fade-delay="0.15"  seconds (default 0)
 */
register("js-fade-up", (el) => {
  const y = parseFloat(el.dataset.fadeY) || 24;
  const delay = parseFloat(el.dataset.fadeDelay) || 0;

  if (REDUCED_MOTION) {
    gsap.set(el, { opacity: 1, y: 0 });
    return;
  }

  gsap.from(el, {
    opacity: 0,
    y,
    delay,
    duration: 0.7,
    ease: "power2.out",
    scrollTrigger: {
      trigger: el,
      start: "top 88%",
      once: true,
    },
  });
});

/**
 * .js-parallax
 * Drifts vertically as you scroll past it, tied to the scrollbar.
 * Desktop only (>= 992px) — below that, columns typically stack and a
 * vertical drift just fights the natural flow. gsap.matchMedia() reverts
 * the tween and kills its ScrollTrigger automatically when the query
 * stops matching, so the element returns to its untransformed position.
 * Optional params:
 *   data-parallax-speed="0.2"   0 = none, 0.5 = strong (default 0.2).
 *                               Negative flips direction.
 *   data-parallax-min="992"     min viewport width in px (default 992).
 *                               Use "0" to run at every width.
 *   data-parallax-trigger=".row" selector for an ancestor to scrub against,
 *                               so sibling elements share one timeline and
 *                               stay in sync (default: the element itself).
 *
 * NOTE: yPercent is relative to each element's OWN height, so a tall
 * element and a short one travel different pixel distances at the same
 * speed. Keep speed low (0.05-0.15) unless the container has room to
 * absorb the drift — overshoot slides content under neighboring
 * sections' opaque backgrounds.
 */
register("js-parallax", (el) => {
  if (REDUCED_MOTION) return;

  const speed = parseFloat(el.dataset.parallaxSpeed) || 0.2;
  const min = el.dataset.parallaxMin ?? 992;
  // Siblings that should drift together must share one scroll timeline —
  // triggering off each element means different heights produce different
  // start/end points, and the pair visibly desyncs.
  const trigger = el.closest(el.dataset.parallaxTrigger || "*") || el;

  gsap.matchMedia().add(`(min-width: ${min}px)`, () => {
    gsap.fromTo(
      el,
      { yPercent: -speed * 100 },
      {
        yPercent: speed * 100,
        ease: "none",
        scrollTrigger: {
          trigger,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      }
    );
  });
});

/**
 * .js-magnetic
 * Element leans toward the cursor and springs back on leave — the Osmo
 * signature. Put it on a button/link. Optional:
 *   data-magnetic-strength="0.4"   how far it follows, ~0–1 (default 0.3)
 */
register("js-magnetic", (el) => {
  if (REDUCED_MOTION) return;

  const strength = parseFloat(el.dataset.magneticStrength) || 0.3;
  // quickTo gives a cheap, reusable tween — ideal for pointermove.
  const xTo = gsap.quickTo(el, "x", { duration: 0.4, ease: "power3.out" });
  const yTo = gsap.quickTo(el, "y", { duration: 0.4, ease: "power3.out" });

  el.addEventListener("pointermove", (e) => {
    const r = el.getBoundingClientRect();
    xTo((e.clientX - (r.left + r.width / 2)) * strength);
    yTo((e.clientY - (r.top + r.height / 2)) * strength);
  });
  el.addEventListener("pointerleave", () => { xTo(0); yTo(0); });
});

/**
 * .js-marquee
 * Seamless infinite ticker. Author ONE set of items inside; the script
 * clones the set so the loop is gapless. Smoothly pauses on hover.
 * Optional:
 *   data-marquee-speed="60"          pixels per second (default 60)
 *   data-marquee-direction="right"   default scrolls left
 * Needs CSS: .js-marquee { overflow: hidden } and a flex track (see below).
 */
register("js-marquee", (el) => {
  const items = Array.from(el.children);
  if (!items.length) return;

  // Move the authored items into a track, then duplicate for the wrap.
  const track = document.createElement("div");
  track.className = "marquee__track";
  items.forEach((n) => track.appendChild(n));
  el.appendChild(track);
  track.append(...Array.from(track.children).map((n) => n.cloneNode(true)));

  if (REDUCED_MOTION) return; // one static set, still readable

  const speed = parseFloat(el.dataset.marqueeSpeed) || 60; // px/sec
  const reverse = el.dataset.marqueeDirection === "right";
  const distance = track.scrollWidth / 2; // width of a single set

  const tween = gsap.fromTo(
    track,
    { x: reverse ? -distance : 0 },
    { x: reverse ? 0 : -distance, duration: distance / speed, ease: "none", repeat: -1 }
  );

  el.addEventListener("pointerenter", () => gsap.to(tween, { timeScale: 0, duration: 0.3 }));
  el.addEventListener("pointerleave", () => gsap.to(tween, { timeScale: 1, duration: 0.3 }));
});

/**
 * .js-counter
 * Counts up to a number when it scrolls into view. Locale-formatted
 * with thousands separators. Optional:
 *   data-counter-to="1200"        target (falls back to the element's text)
 *   data-counter-duration="2"     seconds (default 2)
 *   data-counter-decimals="0"     decimal places (default 0)
 */
register("js-counter", (el) => {
  const to = parseFloat(el.dataset.counterTo ?? el.textContent.replace(/[^0-9.]/g, "")) || 0;
  const decimals = parseInt(el.dataset.counterDecimals) || 0;
  const fmt = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  const render = (v) => { el.textContent = fmt.format(v); };

  if (REDUCED_MOTION) { render(to); return; }

  const obj = { v: 0 };
  gsap.to(obj, {
    v: to,
    duration: parseFloat(el.dataset.counterDuration) || 2,
    ease: "power2.out",
    onUpdate: () => render(obj.v),
    scrollTrigger: { trigger: el, start: "top 85%", once: true },
  });
});

/**
 * .js-stack-cards
 * Direct children become sticky cards that stack; each one recedes
 * (scales down + dims) as the next slides over it. Give the cards an
 * opaque background and some bottom margin so there's scroll distance.
 * Optional:
 *   data-stack-top="64"   sticky offset from the top, in px (default 64)
 */
register("js-stack-cards", (container) => {
  const cards = Array.from(container.children);
  const top = parseFloat(container.dataset.stackTop) || 64;

  cards.forEach((card) => {
    card.style.position = "sticky";
    card.style.top = top + "px";
    card.style.transformOrigin = "50% 0%"; // recede upward, under the next card
  });

  if (REDUCED_MOTION) return; // cards simply stack, no scale

  cards.forEach((card, i) => {
    const next = cards[i + 1];
    if (!next) return; // last card stays full-size
    gsap.to(card, {
      scale: 0.9,
      opacity: 0.6,
      ease: "none",
      scrollTrigger: {
        trigger: next,
        start: "top bottom",     // next card starts entering
        end: "top " + top + "px", // next card reaches the sticky line
        scrub: true,
      },
    });
  });
});

/* ------------------------------------------------------------------ *
 * Boot
 * ------------------------------------------------------------------ */

function boot() {
  initAnimations();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot(); // script loaded after DOM was already parsed
}

// Public API — reach for these from other scripts / after AJAX:
//   window.SiteAnimations.refresh()           // re-scan whole page + recalc
//   window.SiteAnimations.refresh(newNode)    // scope to freshly injected content
//   window.SiteAnimations.init(newNode)       // just wire new nodes, no recalc
//   window.SiteAnimations.register(cls, fn)   // register a new behavior at runtime
window.SiteAnimations = { init: initAnimations, refresh, register };

// esbuild consumers can import these directly instead of the global:
export { initAnimations, refresh, register };

/* ==================================================================
 * COMPANION CSS — copy into your SCSS.
 *
 * IMPORTANT: do NOT add visual rules to the .js-* hooks. Style the
 * element's real classes (.h2, .card, .hero__title). The only CSS the
 * hooks need is functional / anti-flash:
 *
 *   [class*="js-parallax"] { will-change: transform; }
 *   .js-magnetic { display: inline-block; will-change: transform; }
 *
 *   // js-marquee needs a clipped viewport and a non-wrapping flex track.
 *   // (An edge fade-mask is optional but looks great.)
 *   .js-marquee { overflow: hidden; }
 *   .marquee__track { display: flex; width: max-content; }
 *   .js-marquee > .marquee__track > * { flex: none; }
 *
 *   // js-stack-cards: children must be OPAQUE so stacking reads. position
 *   // and top are set in JS from data-stack-top, so don't fight them here.
 *   .js-stack-cards > * { background: #fff; }
 *
 *   // Optional FOUC guard: hide split targets until JS runs. Add a
 *   // `js` class to <html> from your bundle's very first line
 *   // (document.documentElement.classList.add('js')) so no-JS users
 *   // still see the text.
 *   .js .js-reveal-lines,
 *   .js .js-scrub-each-word { visibility: hidden; }
 *   .js .js-reveal-lines .line,
 *   .js .js-scrub-each-word .word { visibility: visible; }
 * ================================================================== */
